/**
 * SearchableCustomerSelect - Searchable dropdown for customer selection
 * Uses server-side search for full dataset coverage
 * Used in Estimates, Jobs, and Invoices (but NOT Visits or Support Tickets)
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { MagnifyingGlass, User, X, SpinnerGap } from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function SearchableCustomerSelect({
  customers: initialCustomers = [],
  value,
  onChange,
  label = "Customer *",
  placeholder = "Search customers...",
  disabled = false,
  "data-testid": testId = "customer-select",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Find selected customer from initial list or search results
  useEffect(() => {
    if (value) {
      // First check initial customers
      const fromInitial = initialCustomers.find(c => c.customer_id === value);
      if (fromInitial) {
        setSelectedCustomer(fromInitial);
        return;
      }
      // Then check search results
      const fromSearch = searchResults.find(c => c.customer_id === value);
      if (fromSearch) {
        setSelectedCustomer(fromSearch);
      }
    } else {
      setSelectedCustomer(null);
    }
  }, [value, initialCustomers, searchResults]);

  // Server-side search function
  const searchCustomers = useCallback(async (term) => {
    if (!term || term.trim().length < 1) {
      // Show initial customers when no search term
      setSearchResults(initialCustomers.slice(0, 20));
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      const params = new URLSearchParams({
        search: term.trim(),
        limit: "20"
      });
      const url = `${API}/customers?${params.toString()}`;
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.customers || []);
      }
    } catch (error) {
      console.error("Error searching customers:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [initialCustomers]);

  // Debounced search on input change
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (isOpen) {
      debounceTimerRef.current = setTimeout(() => {
        searchCustomers(searchTerm);
      }, 200);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm, isOpen, searchCustomers]);

  // Load initial results when dropdown opens
  useEffect(() => {
    if (isOpen && !searchTerm) {
      setSearchResults(initialCustomers.slice(0, 20));
    }
  }, [isOpen, initialCustomers, searchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (customer) => {
    onChange(customer.customer_id);
    setSelectedCustomer(customer);
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
    setSelectedCustomer(null);
    setSearchTerm("");
  };

  // Get display customers (search results or initial)
  const displayCustomers = searchTerm ? searchResults : searchResults.length > 0 ? searchResults : initialCustomers.slice(0, 20);

  return (
    <div ref={containerRef} className="relative">
      {label && <Label>{label}</Label>}
      
      {/* Selected customer display or search input */}
      <div
        className={`mt-1 h-12 border-2 rounded-md flex items-center px-3 cursor-pointer transition-colors ${
          isOpen ? "border-[#FF6600] ring-1 ring-[#FF6600]" : "border-zinc-200 hover:border-zinc-300"
        } ${disabled ? "bg-zinc-100 cursor-not-allowed" : "bg-white"}`}
        onClick={() => !disabled && setIsOpen(true)}
        data-testid={testId}
      >
        {selectedCustomer && !isOpen ? (
          // Show selected customer
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <User size={16} className="text-zinc-400" />
              <span className="font-medium">{selectedCustomer.name}</span>
            </div>
            {!disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 hover:bg-zinc-100 rounded"
              >
                <X size={14} className="text-zinc-400" />
              </button>
            )}
          </div>
        ) : (
          // Show search input
          <div className="flex items-center gap-2 w-full">
            {isSearching ? (
              <SpinnerGap size={16} className="text-zinc-400 flex-shrink-0 animate-spin" />
            ) : (
              <MagnifyingGlass size={16} className="text-zinc-400 flex-shrink-0" />
            )}
            <Input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={placeholder}
              className="border-0 p-0 h-auto focus-visible:ring-0 focus-visible:ring-offset-0"
              disabled={disabled}
              onFocus={() => setIsOpen(true)}
              autoFocus={isOpen}
              data-testid={`${testId}-input`}
            />
          </div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border-2 border-zinc-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {isSearching ? (
            <div className="px-3 py-4 text-center text-zinc-400 text-sm flex items-center justify-center gap-2">
              <SpinnerGap size={16} className="animate-spin" />
              Searching...
            </div>
          ) : displayCustomers.length > 0 ? (
            displayCustomers.map((customer) => (
              <div
                key={customer.customer_id}
                onClick={() => handleSelect(customer)}
                className={`px-3 py-2 cursor-pointer hover:bg-zinc-50 flex items-center gap-2 ${
                  customer.customer_id === value ? "bg-orange-50" : ""
                }`}
                data-testid={`${testId}-option-${customer.customer_id}`}
              >
                <User size={14} className="text-zinc-400" />
                <span>{customer.name}</span>
                {customer.company && (
                  <span className="text-zinc-400 text-sm">({customer.company})</span>
                )}
              </div>
            ))
          ) : (
            <div className="px-3 py-4 text-center text-zinc-400 text-sm">
              {searchTerm ? `No customers found for "${searchTerm}"` : "No customers available"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
